import type { FreightOptionRow, SubmissionDefaults } from '@erp/rpc';
import { pickableOptions } from 'src/components/procurement/draft-purchase-order/draftFreightFormat';

/** The freight option, account and shipping instructions the release dialog starts on. */
export interface Preselected {
  optionId: string | null;
  carrierAccount: string | null;
  instructions: string[];
}

export type ReleaseBaseline =
  | { status: 'failed' }
  | { status: 'loading' }
  | {
      status: 'ready';
      options: FreightOptionRow[];
      suggestedOptionId: string | null;
      preselected: Preselected;
      sending: SubmissionDefaults;
    };

/**
 * What the release dialog starts from, once its reads are in: the vendor's
 * pickable options at the branch, the suggestion for the lines, and the open
 * PO's own freight when lines are added to it, else the suggestion with its
 * account and default shipping instructions. Undefined inputs are still
 * loading.
 */
export function releaseBaseline(inputs: {
  supplierId: string;
  failed: boolean;
  options: FreightOptionRow[] | undefined;
  suggestion: { suggestedOptionId: string | null } | undefined;
  sending: SubmissionDefaults | undefined;
  /** The open PO's freight; null for a new PO. */
  current: Preselected | null | undefined;
}): ReleaseBaseline {
  const { options, suggestion, sending, current } = inputs;
  if (inputs.failed) return { status: 'failed' };
  if (!options || !suggestion || !sending || current === undefined)
    return { status: 'loading' };
  const pickable = pickableOptions(options, inputs.supplierId);
  const { suggestedOptionId } = suggestion;
  const suggested = pickable.find((option) => option.id === suggestedOptionId);
  return {
    status: 'ready',
    options: pickable,
    suggestedOptionId,
    preselected: current ?? {
      optionId: suggestedOptionId,
      carrierAccount: suggested?.carrierAccount ?? null,
      instructions: suggested?.instructions ?? [],
    },
    sending,
  };
}
